<template>
  <!--
    CUB-1287: мастер подключения модуля — шаги из проверки готовности (`procreadiness`): по каждому шагу «готово / не готово»,
    что не так и кнопка «Исправить» сразу на форму исправления; «Назад / Далее» по шагам, «Проверить снова».
  -->
  <div class="cbs-readiness">
    <b-card no-body>
      <b-card-header class="d-flex justify-content-between align-items-center">
        <div>
          <h4 class="mb-0">
            Подключение и проверка готовности
          </h4>
          <small
            v-if="result"
            class="text-muted"
          >
            {{ result.ready ? 'Всё готово' : `Исправить: ${result.problems} из ${result.checks}` }}
          </small>
        </div>
        <b-button
          size="sm"
          variant="outline-primary"
          :disabled="loading"
          @click="run()"
        >
          <b-spinner
            v-if="loading"
            small
            class="mr-50"
          />
          Проверить снова
        </b-button>
      </b-card-header>

      <b-card-body v-if="!loading && result && steps.length">
        <!-- шаги: «готово / не готово» по каждому, переход по клику -->
        <div class="d-flex flex-wrap mb-1">
          <b-button
            v-for="(st, ix) in steps"
            :key="st.group"
            size="sm"
            class="mr-50 mb-50"
            :variant="ix === current ? (st.ok ? 'success' : 'warning') : (st.ok ? 'outline-success' : 'outline-warning')"
            @click="current = ix"
          >
            {{ ix + 1 }}. {{ st.group }}
            <feather-icon
              :icon="st.ok ? 'CheckIcon' : 'AlertTriangleIcon'"
              class="ml-25"
            />
          </b-button>
        </div>

        <h5 class="mb-1">
          Шаг {{ current + 1 }} из {{ steps.length }}: {{ step.group }}
        </h5>
        <b-list-group>
          <b-list-group-item
            v-for="(it, ix) in step.items"
            :key="ix"
            class="d-flex justify-content-between align-items-start"
          >
            <div class="mr-1">
              <b-badge
                :variant="variantOf(it.status)"
                class="mr-50"
              >
                {{ statusText(it.status) }}
              </b-badge>
              <span>{{ it.check }}</span>
              <div
                v-if="it.message"
                class="small text-muted mt-25"
              >
                {{ it.message }}
              </div>
            </div>
            <!-- CUB-1287 п. 3: сразу на форму исправления — только путь внутри платформы -->
            <b-button
              v-if="it.status !== 'ok' && isInternal(it.link)"
              size="sm"
              variant="primary"
              :to="it.link"
            >
              Исправить
            </b-button>
          </b-list-group-item>
        </b-list-group>

        <div class="d-flex justify-content-between mt-1">
          <b-button
            size="sm"
            variant="outline-secondary"
            :disabled="current === 0"
            @click="current -= 1"
          >
            Назад
          </b-button>
          <b-button
            size="sm"
            variant="primary"
            :disabled="current >= steps.length - 1"
            @click="current += 1"
          >
            Далее
          </b-button>
        </div>
      </b-card-body>

      <b-card-body v-else-if="!loading && result && !steps.length">
        Модули домена не объявили проверок готовности.
      </b-card-body>
      <b-card-body v-else-if="!loading && error">
        <b-alert
          show
          variant="danger"
        >
          {{ error }}
        </b-alert>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BCardHeader, BCardBody, BButton, BBadge, BListGroup, BListGroupItem, BSpinner, BAlert,
} from 'bootstrap-vue'
import useCubus from '@/cubus/services/useCubus'

export default {
  name: 'CbsReadiness',
  components: {
    BCard, BCardHeader, BCardBody, BButton, BBadge, BListGroup, BListGroupItem, BSpinner, BAlert,
  },
  data() {
    return {
      loading: false, result: null, error: '', current: 0,
    }
  },
  computed: {
    // шаги в порядке, который задал модуль (группа проверки = шаг настройки)
    steps() {
      const items = (this.result && this.result.items) || []
      const out = []
      items.forEach(it => {
        let st = out.find(s => s.group === it.group)
        if (!st) { st = { group: it.group, items: [], ok: true }; out.push(st) }
        st.items.push(it)
        if (it.status === 'problem') st.ok = false
      })
      return out
    },
    step() { return this.steps[this.current] || { group: '', items: [] } },
  },
  created() { this.run() },
  methods: {
    async run() {
      this.loading = true; this.error = ''
      try {
        const res = await useCubus.runProcedure(this, { objectsid: 'procreadiness', record: {} })
        const r = res && res.result && res.result.items ? res.result : res
        if (!r || !Array.isArray(r.items)) { this.error = 'Проверка готовности не вернула результат.'; this.result = null; return }
        this.result = r
        // открыть первый шаг, где есть что исправить
        const firstBad = this.steps.findIndex(s => !s.ok)
        this.current = firstBad >= 0 ? firstBad : 0
      } finally {
        this.loading = false
      }
    },
    isInternal(link) { return typeof link === 'string' && link.indexOf('/') === 0 && link.indexOf('//') !== 0 },
    variantOf(s) {
      if (s === 'ok') return 'light-success'
      if (s === 'na') return 'light-secondary'
      return 'light-warning'
    },
    statusText(s) {
      if (s === 'ok') return 'готово'
      if (s === 'na') return 'не проверить'
      return 'исправить'
    },
  },
}
</script>
